import { assert, assertUnreachable } from '@disy/lib/custom-error';
import { getDisyNamespace } from '@disy/lib/disy';

import type { FeatureName } from 'cadenza/model/feature-name.gen';
import { isFeatureName } from 'cadenza/model/feature-name.gen';
import { type ThemeFeatureName, isThemeFeature } from 'cadenza/theming-api';

const UI_FEATURE_NAMES = [
  'embedded-dialog-footer-buttons', // show the all dialog buttons
  'embedded-dialog-apply-button', // show the dialog apply button
  'embedded-dialog-cancel-button', // show the dialog cancel button
  /**
   * @see Published CADENZA-33416 Customers may use this to
   * deactivate the designer
   */
  'workbook-design', // allow showing designer
  'workbook-new-view', // show new view button
  'workbook-data-browser', // allow showing data browser
  'workbook-data-view-content', // allows to edit data-view content in the designer
  'workbook-access-settings', // show access settings button
  'workbook-more-actions', // show more ... button in header
  'workbook-snapshots', // show buttons for creating and selecting snapshots
  'workbook-save', // show save/save and publish/save as
  'workbook-worksheet-menu', // show drop-down menu for worksheets
  'workbook-select-version', // allow selecting the version when clicking the versions tag
  'workbook-view-titlebar', // show the title bar of views
  'workbook-view-titlebar-buttons', // show the title bar buttons of views
  'workbook-analysis-context', // enable the analysis context
  'workbook-analysis-context-closeable', // allows to close the analysis context, also shows the close button. On small screens (XS) the analysis context is always closeable
  'workbook-condition-management', // allows to edit/add/remove conditions in views and global analysis context
  'workbook-show-filtered-views', // shows which views are affected by filters
  'workbook-refresh-worksheet', // refreshes the current worksheet
  'workbook-map-toolbar', // show map toolbar
  'workbook-map-find-address', // show find address action
  'workbook-map-find-coordinate', // show find coordinate action
  'workbook-map-find-what3words', // show find what3words action
  'workbook-map-measure', // show measure action
  'workbook-map-location-finder', // show location finder
  'workbook-map-routing', // show button for routing
  'workbook-map-isochrones', // show button for isochrones
  'workbook-map-geolocation', // show button for displaying geolocation
  'workbook-map-timeline-player', // show button for timeline player
  'workbook-map-timeline-3d', // show button for 3d view
  'workbook-map-data-acquisition', // show buttons to create/edit/delete datasets in the map
  /**
   * @see Published CADENZA-37461 Customers may use this to
   * deactivate all "Add layer" features at once
   */
  'workbook-map-add-layer', // show button to add a layer
  'workbook-map-add-layer-from-catalog', // show button to add a layer from the catalog in the map
  'workbook-map-add-layer-from-geodata-service', // show button to add a layer from a geodata service in the map
  'workbook-map-add-data-view-layer', // show button to add a data view layer in the map
  'workbook-map-add-group-layer', // show button to add a group layer in the map
  'workbook-map-add-sketch-layer', // show button to add a sketch layer in the map
  'workbook-map-add-grid-square-layer', // show button to add a grid square layer in the map
  'workbook-map-add-layer-from-data-import', // show button to add a layer from imported data in the map
  'workbook-map-add-data-acquisition-layer', // show button to add a layer from data acquisition template
  'workbook-map-add-analytics-extension-layer', // show button to add a layer from analytics extension

  /**
   * @see Published CADENZA-33416 Customers may use this to
   * deactivate editing of WB layout/design
   */
  'workbook-view-management', // allows to edit/add/remove views, data views
  'workbook-drillthrough-same-workbook', // show drillthroughs to the same workbook
  'workbook-drillthrough-other-workbooks', // show drillthroughs to other workbooks
  'workbook-drillthrough-external-links', // show drillthroughs to external links
  'workbook-drillthrough-postmessage', // show drillthroughs sent via postMessage
  // export
  'workbook-pdf-export', // export the whole workbook as a PDF report
  'workbook-print-worksheet',
  'workbook-worksheet-pdf-export',
  'workbook-view-pdf-export',
  'workbook-view-data-and-images-export',
  'workbook-tables-excel-export',
  'workbook-worksheet-tables-excel-export',
  // allows to create/duplicate/edit worksheet, click on version tag
  'workbook-management',
  'workbook-layout-management', // allows to edit workbook layout, like resizing and dragging views
  'workbook-map-view-sidebar-resize', // allows resizing the legend sidebar of a map view
  'workbook-data-view-analysis-context', // allows displaying the analysis context of a data view
  'workbook-map-data-export', // allows exporting map data ('KML_EXPORT', 'SHAPEFILE_EXPORT', 'EXPORT_GEOPACKAGE')
  'workbook-object-info-address', // enables geocoding functionality in object info flyout
  'workbook-table-cell-details',
  'workbook-table-single-measure-values',
  'report-management',
  'workbook-zip-export',
  'spatial-condition-management',
  'workbook-selection-cart', // hide selection cart in workbook (currently only for internal usage in embedding target preview)
] as const;

export type UiFeatureName = (typeof UI_FEATURE_NAMES)[number];
export type WorkbookUiFeatureName = UiFeatureName & `workbook-${string}`;

export type AllFeatures = FeatureName | UiFeatureName | ThemeFeatureName;

/**
 * The keys must not be of type FeatureName, because the dependencies
 * between the ICadenzaFeatures are modeled already in the backend.
 */
type FeatureDependencies = { [key in AllFeatures]?: AllFeatures[] };

/**
 * Defines which features depend on others.
 * When adding new dependent features here, please check that there are not loops - features
 * should not depend on each other.
 * Example:
 * 'workbook-view-management': [ 'workbook-design' ] means that 'workbook-view-management' is available only when 'workbook-design' is.
 */
const FEATURE_DEPENDENCIES: FeatureDependencies = {
  'workbook-analysis-context-closeable': ['workbook-analysis-context'],
  'workbook-zip-export': ['workbook-management', 'WORKBOOK_ZIP_EXPORT_IMPORT'],
  'report-management': ['workbook-management', 'workbook-view-management', 'EDIT_REPORT'],
  'spatial-condition-management': ['workbook-view-management', 'WORKBOOK_EDIT_SPATIAL_CONDITION'],
  'workbook-access-settings': ['workbook-management'],
  'workbook-data-view-content': ['workbook-design'],
  'workbook-layout-management': ['workbook-design'],
  'workbook-new-view': ['workbook-view-management'],
  'workbook-save': ['workbook-management'],
  'workbook-select-version': ['workbook-management'],
  'workbook-snapshots': ['WORKBOOK_PERSONAL_SNAPSHOTS', 'WORKBOOK_TEMPORARY_SNAPSHOTS'],
  'workbook-view-management': ['workbook-design'],
  'workbook-management': ['workbook-design'],
  'workbook-map-find-address': ['workbook-map-toolbar', 'REVERSE_GEOCODING'],
  'workbook-map-find-coordinate': ['workbook-map-toolbar'],
  'workbook-map-find-what3words': ['workbook-map-toolbar', 'WHAT3WORDS'],
  'workbook-map-measure': ['workbook-map-toolbar'],
  'workbook-map-location-finder': ['workbook-map-toolbar', 'LOCATION_FINDER'],
  'workbook-map-routing': ['workbook-map-toolbar', 'CALCULATE_ROUTES'],
  'workbook-map-isochrones': ['workbook-map-toolbar', 'CALCULATE_ISOCHRONES'],
  'workbook-map-timeline-player': ['workbook-map-toolbar', 'TIMELINE'],
  'workbook-map-timeline-3d': ['workbook-map-toolbar', 'TIMELINE_3D'],
  'workbook-map-data-acquisition': ['workbook-map-toolbar', 'ACQUIRE_OBJECTTYPE_DATA'],
  'workbook-map-add-layer-from-catalog': ['workbook-map-add-layer'],
  'workbook-map-add-layer-from-geodata-service': ['workbook-map-add-layer', 'ADD_EXTERNAL_LAYERS'],
  'workbook-map-add-data-view-layer': ['workbook-map-add-layer'],
  'workbook-map-add-group-layer': ['workbook-map-add-layer'],
  'workbook-map-add-sketch-layer': ['workbook-map-add-layer', 'CREATE_SKETCH_LAYER'],
  'workbook-map-add-grid-square-layer': ['workbook-map-add-layer'],
  'workbook-map-add-layer-from-data-import': ['workbook-map-add-layer', 'SELF_SERVICE_IMPORT'],
  'workbook-map-add-data-acquisition-layer': ['workbook-map-add-layer', 'CREATE_OBJECTTYPE_BASED_ON_TEMPLATE'],
  'workbook-pdf-export': ['CREATE_WORKBOOK_REPORT'],
  'workbook-worksheet-pdf-export': ['CREATE_WORKBOOK_REPORT'],
  'workbook-view-pdf-export': ['CREATE_WORKBOOK_REPORT'],
  'workbook-object-info-address': ['REVERSE_GEOCODING'],
  'workbook-tables-excel-export': ['EXCEL_EXPORT'],
  'workbook-worksheet-tables-excel-export': ['EXCEL_EXPORT'],
  'workbook-data-view-analysis-context': ['workbook-view-titlebar-buttons'],
  CALCULATE_ROUTES: ['LOCATION_FINDER'],
  CALCULATE_ISOCHRONES: ['LOCATION_FINDER'],
};

/**
 * Checks the availability of a Cadenza feature for the current user.
 *
 * The feature must be enabled on platform level and can be disabled on page, theme or request level.
 * Unfortunately, the feature names are different on each level, and we cannot change that, because
 * some are published. So we also check the synonyms of the given feature name. If the feature
 * depends on other features, then all other features must be available too.
 *
 * NOTE: Doesn't check workbook features - also in dependencies workbook features are omitted.
 * If you are in workbook, then isWorkbookFeatureAvailable is preferred unless you have a good
 * reason to not check workbook feature dependencies.
 *
 * @param featureName - The name of the feature to check (on any level)
 * @return Whether the feature is available.rt
 */
export function isFeatureAvailable(featureName: AllFeatures): boolean {
  assert(
    !isWorkbookFeature(featureName),
    'Cannot check workbook feature with a isFeatureAvailable. Use isWorkbookFeatureAvailable instead.',
  );
  return (
    isSingleFeatureAvailable(featureName) &&
    getDependentFeatures(featureName)
      .filter((dependentFeatureName) => !isWorkbookFeature(dependentFeatureName))
      .every(isFeatureAvailable)
  );
}

export function getDependentFeatures(featureName: AllFeatures) {
  return FEATURE_DEPENDENCIES[featureName] ?? [];
}

/**
 * Checks if feature is available without checking dependencies.
 * @param featureName
 * @deprecated This is exported only for use in the {@link import('cadenza/workbook/workbook-features').isWorkbookFeatureAvailable|isWorkbookFeatureAvailable}.
 * Use {@link isFeatureAvailable} instead.
 */
export function isSingleFeatureAvailable(featureName: AllFeatures) {
  if (isFeatureName(featureName)) {
    return isFeatureAvailableInPlatform(featureName);
  }
  if (isUiFeature(featureName)) {
    return isFeatureAvailableInPage(featureName);
  }
  if (isThemeFeature(featureName)) {
    return isFeatureAvailableInTheme(featureName);
  }
  assertUnreachable(featureName);
}

function isUiFeature(featureName: string): featureName is UiFeatureName {
  return UI_FEATURE_NAMES.includes(featureName as never);
}

export function isWorkbookFeature(feature: string): feature is WorkbookUiFeatureName {
  return isUiFeature(feature) && feature.startsWith('workbook');
}

function isFeatureAvailableInTheme(featureName: ThemeFeatureName) {
  return getDisyNamespace().theme?.features[featureName] !== false;
}

function isFeatureAvailableInPage(featureName: UiFeatureName) {
  return !getDisyNamespace().disabledUiFeatures?.includes(featureName);
}

function isFeatureAvailableInPlatform(featureName: FeatureName) {
  return getDisyNamespace().availableFeatures?.includes(featureName) ?? false;
}
